import React, { useState, useEffect } from 'react';
import { Input, Button, Checkbox, Modal, Select, Tag, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getTodos, createTodo, updateTodo, toggleTodo, deleteTodo } from '../services/todoService';

const { TextArea } = Input;
const { Option } = Select;

const TodoPanel = ({ isOpen, onToggle }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, completed
  const [editingTodo, setEditingTodo] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Load todos on component mount
  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    setLoading(true);
    try {
      const data = await getTodos();
      setTodos(data);
    } catch (error) {
      toast.error('Failed to load todos');
      console.error('Error fetching todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTodo = async () => {
    if (!newTodoTitle.trim()) {
      toast.warning('Please enter a task title');
      return;
    }

    try {
      const newTodo = await createTodo({
        title: newTodoTitle,
        priority: 'medium'
      });
      setTodos([...todos, newTodo]);
      setNewTodoTitle('');
      toast.success('Task created');
    } catch (error) {
      toast.error('Failed to create task');
      console.error('Error creating todo:', error);
    }
  };

  const handleToggleTodo = async (id) => {
    try {
      const updatedTodo = await toggleTodo(id);
      setTodos(todos.map(todo => todo.id === id ? updatedTodo : todo));
    } catch (error) {
      toast.error('Failed to update task');
      console.error('Error toggling todo:', error);
    }
  };

  const handleDeleteTodo = async (id) => {
    try {
      await deleteTodo(id);
      setTodos(todos.filter(todo => todo.id !== id));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
      console.error('Error deleting todo:', error);
    }
  };

  const handleOpenEditModal = (todo) => {
    setEditingTodo({
      id: todo.id,
      title: todo.title,
      description: todo.description || '',
      priority: todo.priority,
      completed: todo.completed
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTodo.title.trim()) {
      toast.warning('Task title cannot be empty');
      return;
    }

    try {
      const updatedTodo = await updateTodo(editingTodo.id, {
        title: editingTodo.title,
        description: editingTodo.description,
        priority: editingTodo.priority,
        completed: editingTodo.completed
      });
      setTodos(todos.map(todo => todo.id === editingTodo.id ? updatedTodo : todo));
      setIsEditModalOpen(false);
      setEditingTodo(null);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
      console.error('Error updating todo:', error);
    }
  };

  const filteredTodos = todos.filter(todo => {
    if (filter === 'active') return !todo.completed;
    if (filter === 'completed') return todo.completed;
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'High';
      case 'medium': return 'Medium';
      case 'low': return 'Low';
      default: return 'Medium';
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed right-0 top-20 z-40">
        <button
          onClick={onToggle}
          className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-l-lg shadow-lg transition-all"
          title="Open Todo List"
        >
          <MenuOutlined className="text-lg" />
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Todo Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-80 bg-white border-l border-gray-200 shadow-xl z-40 flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Todo List</h2>
          <button
            onClick={onToggle}
            className="text-white hover:bg-blue-700 p-1 rounded transition-colors"
            title="Close Todo List"
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Quick Add Form */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-2">
            <Input
              placeholder="Add a new task..."
              value={newTodoTitle}
              onChange={(e) => setNewTodoTitle(e.target.value)}
              onPressEnter={handleCreateTodo}
              className="flex-1"
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateTodo}
              disabled={!newTodoTitle.trim()}
            >
              Add
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setFilter('all')}
          >
            All ({todos.length})
          </button>
          <button
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              filter === 'active'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setFilter('active')}
          >
            Active ({todos.filter(t => !t.completed).length})
          </button>
          <button
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              filter === 'completed'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setFilter('completed')}
          >
            Done ({todos.filter(t => t.completed).length})
          </button>
        </div>

        {/* Todo List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Spin />
            </div>
          ) : filteredTodos.length === 0 ? (
            <div className="text-center text-gray-400 mt-8">
              <p>No tasks found</p>
              {filter !== 'all' && (
                <button
                  onClick={() => setFilter('all')}
                  className="text-blue-600 hover:underline mt-2"
                >
                  View all tasks
                </button>
              )}
            </div>
          ) : (
            filteredTodos.map(todo => (
              <div
                key={todo.id}
                className={`p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow ${
                  todo.completed ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={todo.completed}
                    onChange={() => handleToggleTodo(todo.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-medium ${
                        todo.completed ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}
                    >
                      {todo.title}
                    </div>
                    {todo.description && (
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {todo.description}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Tag color={getPriorityColor(todo.priority)} className="text-xs m-0">
                        {getPriorityLabel(todo.priority)}
                      </Tag>
                      {todo.created_by_username && (
                        <span className="text-xs text-gray-400">
                          by {todo.created_by_username}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleOpenEditModal(todo)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit"
                    >
                      <EditOutlined />
                    </button>
                    <button
                      onClick={() => handleDeleteTodo(todo.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Delete"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        title="Edit Task"
        open={isEditModalOpen}
        onOk={handleSaveEdit}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingTodo(null);
        }}
        okText="Save"
        cancelText="Cancel"
      >
        {editingTodo && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <Input
                value={editingTodo.title}
                onChange={(e) => setEditingTodo({ ...editingTodo, title: e.target.value })}
                placeholder="Task title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <TextArea
                value={editingTodo.description}
                onChange={(e) => setEditingTodo({ ...editingTodo, description: e.target.value })}
                placeholder="Task description (optional)"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <Select
                value={editingTodo.priority}
                onChange={(value) => setEditingTodo({ ...editingTodo, priority: value })}
                className="w-full"
              >
                <Option value="low">
                  <Tag color="green" className="m-0">Low</Tag>
                </Option>
                <Option value="medium">
                  <Tag color="orange" className="m-0">Medium</Tag>
                </Option>
                <Option value="high">
                  <Tag color="red" className="m-0">High</Tag>
                </Option>
              </Select>
            </div>
            <div>
              <Checkbox
                checked={editingTodo.completed}
                onChange={(e) => setEditingTodo({ ...editingTodo, completed: e.target.checked })}
              >
                Mark as completed
              </Checkbox>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TodoPanel;
